import React, { useState, useCallback, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import WinrateChart from './WinrateChart';
import type { WinratePoint } from './WinrateChart';
import GoBoard from './GoBoard';
import RightPanelTabs from './RightPanelTabs';
import { gameStore } from '../store/gameStoreMobx';
import { useKataGo } from '../hooks/useKataGo';
import { api } from '../services/api';
import type { Point, Move } from '../types/game';
import { pointToSGF } from '../services/coordinateUtils';
import { moves2sgf, downloadSgf, sgf2list, readFileAsText } from '../services/sgf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SystemStatus from './SystemStatus';
import { useLocale } from '../i18n';
import { faBackwardStep, faBackward, faChevronLeft, faChevronRight, faForward, faForwardStep, faRotateLeft, faHand, faRobot, faStar, faFolderOpen, faFloppyDisk, faCodeBranch, faSpinner, faPlus, faArrowsRotate, faBookmark } from '@fortawesome/free-solid-svg-icons';
import BookmarkDialog from './BookmarkDialog';
import { saveBookmark } from '../services/bookmarkStorage';
import { collectionNavStore } from '../store/collectionNavStore';
import { bookmarkNavStore } from '../store/bookmarkNavStore';

const GamePlay: React.FC = () => {
  // MobX store - no destructuring needed, direct access
  const store = gameStore;

  const { getMove, getScore, error: kataError } = useKataGo();
  const { t } = useLocale();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [isPlayMode, setIsPlayMode] = useState(false);
  const [showMobilePanel, setShowMobilePanel] = useState(false);

  const [showNewGameDialog, setShowNewGameDialog] = useState(false);
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [bookmarkRefreshKey, setBookmarkRefreshKey] = useState(0);
  const [selectedMode, setSelectedMode] = useState<'play' | 'study'>('study');
  const [selectedHandicap, setSelectedHandicap] = useState(() => {
    const saved = localStorage.getItem('deepgo_handicap');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [selectedKomi, setSelectedKomi] = useState(() => {
    const saved = localStorage.getItem('deepgo_komi');
    return saved !== null ? parseFloat(saved) : 7.5;
  });
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // File input ref for loading SGF
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track previous move count for self-play delay
  const prevMovesLengthRef = useRef<number>(0);

  // Keep last known score/emoji visible while bot is thinking (reset at position 0)
  const lastScoreStringRef = useRef<string>('');
  const lastMoveEmojiRef = useRef<string>('');
  if (store.currentPosition === 0) {
    lastScoreStringRef.current = '';
    lastMoveEmojiRef.current = '';
  } else {
    if (store.scoreString) lastScoreStringRef.current = store.scoreString;
    if (store.moveEmoji) lastMoveEmojiRef.current = store.moveEmoji;
  }

  // Track previous position for replay delay
  // Responsive board size
  const boardContainerRef = useRef<HTMLDivElement>(null);
  const [boardSize, setBoardSize] = useState(() => Math.min(window.innerWidth - 20, 480));
  useEffect(() => {
    const container = boardContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      setBoardSize(Math.min(width, 480));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Inject API function into store on mount
  useEffect(() => {
    store.setGetMoveApi(getMove);
  }, [getMove, store]);

  // Handle user click on board
  const handleIntersectionClick = useCallback(
    async (point: Point) => {
      if (!store.canPlayMove || !store.canPlayAt(point)) {
        return;
      }

      const sgfMove = pointToSGF(point, store.boardSize);
      const humanMove: Move = {
        mv: sgfMove,
        agent: 'human',
      };

      // Store handles everything: move, analysis, and bot response
      await store.playHumanMove(humanMove);
    },
    [store]
  );

  // Create new game
  const handleNewGame = useCallback(async () => {
    try {
      const response = await api.createGame({
        handicap: selectedHandicap,
        komi: selectedKomi,
      });

      store.setGameHash(response.game_hash);
      store.newGame(selectedHandicap, selectedKomi, store.boardSize);
      setIsPlayMode(selectedMode === 'play');
      setShowNewGameDialog(false);
      store.setSelfPlaying(false);

      // If handicap game, white (bot) plays first
      if (selectedHandicap >= 2 && !store.settings.disable_ai) {
        store.requestBotMove();
      }
    } catch (err) {
      store.setError(err instanceof Error ? err.message : t('failedToCreateGame'));
    }
  }, [selectedHandicap, selectedKomi, selectedMode, store]);

  // Pass move
  const handlePass = useCallback(async () => {
    // Store handles everything: move, analysis, and bot response
    await store.playHumanPass();
  }, [store]);


  // Get current score
  const handleGetScore = useCallback(async () => {
    const moveList = store.getMoveList();
    await getScore(store.boardSize, moveList);
  }, [store, getScore]);

  // Handle Best button click
  const handleToggleBestMoves = useCallback(async () => {
    if (store.showBestMovesOnBoard) {
      store.clearBestMoves();
    } else {
      // Clear alternatives first — mutually exclusive
      store.clearAlternativeMoves();
      store.clearBestMoves();
      store.toggleBestMovesOnBoard();

      const moveList = store.getMoveList();
      const response = await getMove(store.boardSize, moveList, store.komi, store.handicap);
      if (response) {
        store.setBestMoves(response.diagnostics.best_ten);
      }
    }
  }, [store, getMove]);

  // Handle Alternatives button click - shows alternatives for current move
  const handleToggleAlternativeMoves = useCallback(async () => {
    if (store.showAlternativeMovesOnBoard) {
      store.clearAlternativeMoves();
    } else {
      if (store.currentPosition === 0) return;

      // Clear best moves first — mutually exclusive
      store.clearBestMoves();
      store.clearAlternativeMoves();
      store.toggleAlternativeMovesOnBoard();

      const moveList = store.moves.slice(0, store.currentPosition - 1).map(m => m.mv);
      const response = await getMove(store.boardSize, moveList, store.komi, store.handicap);
      if (response) {
        store.setAlternativeMoves(response.diagnostics.best_ten);
      }
    }
  }, [store, getMove]);

  // Save game as SGF
  const handleSaveSgf = useCallback(() => {
    if (store.moves.length === 0) {
      store.setError(t('noMovesToSave'));
      return;
    }

    const metadata = {
      pb: 'Black',
      pw: 'White',
      km: store.komi.toString(),
      dt: new Date().toISOString().slice(0, 10),
      aiAnalysis: store.badMoves.length > 0 ? store.createAiAnalysisData() : undefined,
    };

    const sgf = moves2sgf(store.moves, metadata);
    const filename = `game-${new Date().getTime()}.sgf`;
    downloadSgf(filename, sgf);
  }, [store]);

  const [isDragOver, setIsDragOver] = useState(false);

  // Core SGF loading logic — works with raw text (used by file load, drop, and collection nav)
  const loadSgfText = useCallback(async (sgfText: string) => {
    try {
      const parsed = sgf2list(sgfText);
      const szMatch = /SZ\[(\d+)\]/.exec(sgfText);
      const boardSize = (szMatch ? parseInt(szMatch[1]) : 19) as any;

      store.newGame(0, parsed.komi, boardSize);
      store.setGameMetadata(parsed.pb, parsed.pw, parsed.RE, parsed.DT);

      for (let i = 0; i < parsed.moves.length; i++) {
        const move: Move = {
          mv: parsed.moves[i],
          p: (parseFloat(parsed.probs[i]) || 0) / 100,
          score: parseFloat(parsed.scores[i]) || 0,
          agent: 'human',
          comment: parsed.comments[i] || '',
        };
        store.addMove(move);
      }

      if (parsed.aiAnalysis && parsed.aiAnalysis.badMoves.length > 0) {
        const loadedBadMoves = parsed.aiAnalysis.badMoves
          .map(bm => ({ moveNumber: bm.moveNumber, move: store.moves[bm.moveNumber - 1], badness: bm.badness }))
          .filter(bm => bm.move);
        store.setLoadedBadMoves(loadedBadMoves);
        if (parsed.aiAnalysis.threshold) store.setBadMovesThreshold(parsed.aiAnalysis.threshold);
      }

      store.setError(null);
    } catch (err) {
      store.setError(err instanceof Error ? err.message : t('failedToLoadSgf'));
    }
  }, [store, t]);

  const loadSgfFile = useCallback(async (file: File) => {
    const text = await readFileAsText(file);
    await loadSgfText(text);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [loadSgfText]);

  // File input change handler
  const handleLoadSgf = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) await loadSgfFile(file);
  }, [loadSgfFile]);

  // Restore a bookmark into the game store
  const restoreBookmark = useCallback((b: NonNullable<typeof bookmarkNavStore.currentBookmark>) => {
    store.newGame(b.handicap, b.komi, b.boardSize as any);
    store.setGameMetadata(b.playerBlack, b.playerWhite, '', '');
    for (const move of b.moves as Move[]) store.addMove(move);
    store.goToMove(b.moveNumber);
  }, [store]);

  // Load game from nav context on mount
  useEffect(() => {
    if (bookmarkNavStore.isActive && bookmarkNavStore.currentBookmark) {
      restoreBookmark(bookmarkNavStore.currentBookmark);
    } else if (collectionNavStore.isActive && collectionNavStore.currentGame) {
      loadSgfText(collectionNavStore.currentGame.sgfText);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.sgf')) return;
    await loadSgfFile(file);
  }, [loadSgfFile]);

  const handleToggleBotMode = useCallback(() => {
    store.updateSettings({ disable_ai: !store.settings.disable_ai });
  }, [store]);

  const handleCollectionPrev = useCallback(async () => {
    collectionNavStore.prev();
    if (collectionNavStore.currentGame) await loadSgfText(collectionNavStore.currentGame.sgfText);
  }, [loadSgfText]);

  const handleCollectionNext = useCallback(async () => {
    collectionNavStore.next();
    if (collectionNavStore.currentGame) await loadSgfText(collectionNavStore.currentGame.sgfText);
  }, [loadSgfText]);

  const handleBookmarkPrev = useCallback(() => {
    bookmarkNavStore.prev();
    if (bookmarkNavStore.currentBookmark) restoreBookmark(bookmarkNavStore.currentBookmark);
  }, [restoreBookmark]);

  const handleBookmarkNext = useCallback(() => {
    bookmarkNavStore.next();
    if (bookmarkNavStore.currentBookmark) restoreBookmark(bookmarkNavStore.currentBookmark);
  }, [restoreBookmark]);

  const handleSaveBookmark = useCallback(async (tag: string, comment: string) => {
    setShowBookmarkDialog(false);
    try {
      await saveBookmark({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        tag,
        comment,
        moveNumber: store.currentPosition,
        // JSON round-trip strips MobX observable proxies → plain objects
        moves: JSON.parse(JSON.stringify(store.moves)),
        boardSize: store.boardSize,
        komi: store.komi,
        handicap: store.handicap,
        playerBlack: store.computedPlayerBlack,
        playerWhite: store.computedPlayerWhite,
        createdAt: Date.now(),
      });
      setBookmarkRefreshKey(k => k + 1);
    } catch (e) {
      console.error('Failed to save bookmark:', e);
      store.setError('Failed to save bookmark');
    }
  }, [store]);

  // Navigate to next move and request AI analysis if the move has no data
  const handleNextMove = useCallback(async () => {
    store.nextMove();
    const pos = store.currentPosition;
    const move = store.moves[pos - 1];
    if (move && !move.p && !store.settings.disable_ai && !store.isWaitingForBot) {
      const moveList = store.getMoveList();
      const response = await getMove(store.boardSize, moveList, store.komi, store.handicap);
      if (response?.diagnostics) {
        store.updateMoveAnalysis(pos, response.diagnostics.winprob, response.diagnostics.score, response.diagnostics);
      }
    }
  }, [store, getMove]);

  // Navigate to a specific move from bad moves list
  const handleBadMoveClick = useCallback((moveNumber: number) => {
    store.goToMove(moveNumber);
  }, [store]);

  // Self-play loop with 2 second delay (except first move)
  useEffect(() => {
    console.log('Self-play effect triggered', { isSelfPlaying: store.isSelfPlaying, isWaitingForBot: store.isWaitingForBot });
    if (store.shouldContinueSelfPlay()) {
      if (store.checkSelfPlayStop()) {
        return;
      }

      // Check if this is the first move (no change in moves count)
      const isFirstMove = store.moves.length === prevMovesLengthRef.current;
      const delay = isFirstMove ? 0 : 2000; // No delay for first move, 2s for subsequent moves

      console.log(isFirstMove ? 'Requesting bot move immediately...' : 'Requesting bot move in 2 seconds...');
      const timer = setTimeout(() => {
        store.requestBotMove();
      }, delay);

      // Update ref for next iteration
      prevMovesLengthRef.current = store.moves.length;

      return () => clearTimeout(timer);
    }
  }, [store.isSelfPlaying, store.moves, store.isWaitingForBot, store]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Intercept ArrowRight (non-ctrl) to use handleNextMove with AI analysis
      if (e.key === 'ArrowRight' && !e.ctrlKey) {
        e.preventDefault();
        handleNextMove();
        return;
      }

      // Handle basic navigation shortcuts via store
      if (store.handleKeyboardShortcut(e.key, e.ctrlKey)) {
        e.preventDefault();
        return;
      }

      // Handle remaining shortcuts that need component functions
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (store.shouldRequestBotMove()) {
            store.requestBotMove();
          }
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          handlePass();
          break;
        case 's':
        case 'S':
          e.preventDefault();
          handleGetScore();
          break;
        case 'b':
        case 'B':
          e.preventDefault();
          handleToggleBestMoves();
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          handleToggleAlternativeMoves();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [store, handlePass, handleGetScore, handleToggleBestMoves, handleToggleAlternativeMoves, handleNextMove]);

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', position: 'relative', overflow: 'hidden' }}>
      {/* Health indicator - shows red dot when there's an error */}
      {(store.error || kataError) && (
        <>
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              width: '12px',
              height: '12px',
              backgroundColor: '#dc3545',
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 0 8px rgba(220, 53, 69, 0.6)',
            }}
            onClick={() => setShowErrorDetails(!showErrorDetails)}
            title={store.error || kataError || ''}
          />
          {showErrorDetails && (
            <div
              style={{
                position: 'absolute',
                bottom: '40px',
                right: '20px',
                backgroundColor: 'white',
                border: '2px solid #dc3545',
                borderRadius: '4px',
                padding: '12px',
                maxWidth: '300px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 1000,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong style={{ color: '#dc3545', fontSize: '14px' }}>{t('error')}</strong>
                <button
                  onClick={() => setShowErrorDetails(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: 1,
                  }}
                  title={t('close')}
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: '13px', color: '#333' }}>{store.error || kataError}</div>
            </div>
          )}
        </>
      )}

      {/* Main content wrapper */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* Top controls row — sits above board+panel, spans full content width */}
        <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', width: '100%', maxWidth: isMobile ? '100%' : '780px' }}>
            {bookmarkNavStore.isActive ? (
              // ── Bookmark navigation mode ───────────────────────────────
              <>
                {bookmarkNavStore.currentBookmark && (
                  <span style={{ fontSize: '13px', color: '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                    🔖 {bookmarkNavStore.currentBookmark.tag || 'Bookmark'}
                  </span>
                )}
                <span style={{ fontSize: '12px', color: '#aaa', whiteSpace: 'nowrap' }}>
                  {bookmarkNavStore.currentIndex + 1} / {bookmarkNavStore.bookmarks.length}
                </span>
                <button onClick={handleBookmarkPrev} disabled={!bookmarkNavStore.hasPrev}
                  style={{ padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: bookmarkNavStore.hasPrev ? 'pointer' : 'default', color: bookmarkNavStore.hasPrev ? '#333' : '#ccc', fontSize: '16px', lineHeight: 1 }}>
                  ‹
                </button>
                <button onClick={handleBookmarkNext} disabled={!bookmarkNavStore.hasNext}
                  style={{ padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: bookmarkNavStore.hasNext ? 'pointer' : 'default', color: bookmarkNavStore.hasNext ? '#333' : '#ccc', fontSize: '16px', lineHeight: 1 }}>
                  ›
                </button>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', background: '#e2e3e5', borderRadius: '8px', padding: '3px', fontSize: '13px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                  <button onClick={() => setIsPlayMode(false)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: !isPlayMode ? 600 : 400, background: !isPlayMode ? '#fff' : 'transparent', color: !isPlayMode ? '#111' : '#999', boxShadow: !isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    {t('studyMode')}
                  </button>
                  <button onClick={() => setIsPlayMode(true)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: isPlayMode ? 600 : 400, background: isPlayMode ? '#fff' : 'transparent', color: isPlayMode ? '#111' : '#999', boxShadow: isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    Game
                  </button>
                </div>
              </>
            ) : collectionNavStore.isActive ? (
              // ── Collection navigation mode ─────────────────────────────
              <>
                {collectionNavStore.currentGame && (
                  <span style={{ fontSize: '13px', color: '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                    {collectionNavStore.currentGame.playerBlack} vs {collectionNavStore.currentGame.playerWhite}
                  </span>
                )}
                <span style={{ fontSize: '12px', color: '#aaa', whiteSpace: 'nowrap' }}>
                  {collectionNavStore.currentIndex + 1} / {collectionNavStore.games.length}
                </span>
                <button onClick={handleCollectionPrev} disabled={!collectionNavStore.hasPrev}
                  style={{ padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: collectionNavStore.hasPrev ? 'pointer' : 'default', color: collectionNavStore.hasPrev ? '#333' : '#ccc', fontSize: '16px', lineHeight: 1 }}>
                  ‹
                </button>
                <button onClick={handleCollectionNext} disabled={!collectionNavStore.hasNext}
                  style={{ padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: collectionNavStore.hasNext ? 'pointer' : 'default', color: collectionNavStore.hasNext ? '#333' : '#ccc', fontSize: '16px', lineHeight: 1 }}>
                  ›
                </button>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', background: '#e2e3e5', borderRadius: '8px', padding: '3px', fontSize: '13px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                  <button onClick={() => setIsPlayMode(false)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: !isPlayMode ? 600 : 400, background: !isPlayMode ? '#fff' : 'transparent', color: !isPlayMode ? '#111' : '#999', boxShadow: !isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    {t('studyMode')}
                  </button>
                  <button onClick={() => setIsPlayMode(true)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: isPlayMode ? 600 : 400, background: isPlayMode ? '#fff' : 'transparent', color: isPlayMode ? '#111' : '#999', boxShadow: isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    Game
                  </button>
                </div>
              </>
            ) : (
              // ── Normal game controls mode ──────────────────────────────
              <>
                <button className="btn" onClick={() => setShowNewGameDialog(true)} disabled={store.isSelfPlaying} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FontAwesomeIcon icon={faPlus} />
                  {t('newGame')}
                </button>
                <button className="btn" onClick={() => fileInputRef.current?.click()} disabled={store.isSelfPlaying} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FontAwesomeIcon icon={faFolderOpen} />
                  {t('load')}
                </button>
                <button className="btn" onClick={handleSaveSgf} disabled={store.isSelfPlaying} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  {t('save')}
                </button>
                {/* Study / Game mode toggle — sliding pill */}
                <div style={{ display: 'flex', background: '#e2e3e5', borderRadius: '8px', padding: '3px', gap: '0', fontSize: '13px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                  <button onClick={() => setIsPlayMode(false)} style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: !isPlayMode ? 600 : 400, background: !isPlayMode ? '#fff' : 'transparent', color: !isPlayMode ? '#111' : '#666', boxShadow: !isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    {t('studyMode')}
                  </button>
                  <button onClick={() => setIsPlayMode(true)} style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: isPlayMode ? 600 : 400, background: isPlayMode ? '#fff' : 'transparent', color: isPlayMode ? '#111' : '#666', boxShadow: isPlayMode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                    Game
                  </button>
                </div>
                {isMobile && !isPlayMode && (
                  <button className="btn" onClick={() => setShowMobilePanel(true)}>☰</button>
                )}
              </>
            )}
        </div>

        {/* Board + right panel row — right panel top-aligns with board */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', alignItems: 'flex-start', width: '100%', maxWidth: isMobile ? '100%' : '780px', justifyContent: 'center' }}>

          {/* Board */}
          <div ref={boardContainerRef} style={{ width: '100%', maxWidth: isMobile ? '100%' : '480px', flexShrink: 0 }}>
            <div
              style={{ flexShrink: 0, position: 'relative' }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragOver && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(74,144,226,0.15)', border: '2px dashed #4a90e2', borderRadius: '8px', pointerEvents: 'none' }}>
                  <span style={{ color: '#4a90e2', fontWeight: 600, fontSize: '15px', background: 'rgba(255,255,255,0.9)', padding: '6px 14px', borderRadius: '6px' }}>Drop SGF to load</span>
                </div>
              )}
              <GoBoard
                size={store.boardSize}
                stones={store.boardState}
                marks={store.allMarks}
                onIntersectionClick={handleIntersectionClick}
                showHover={store.shouldShowHover}
                nextPlayer={store.nextPlayer}
                width={boardSize}
                height={boardSize}
              />

              {/* Controls bar (study mode only) */}
              {!isPlayMode && <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {/* Row 1: AI + nav + actions | bot toggle (desktop only) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="btn-icon-group">
                      <button onClick={() => store.requestBotMove()} disabled={store.isSelfPlaying} title={t('aiPlay')} style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: store.isSelfPlaying ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333333', fontSize: '13px', fontWeight: 'bold' }}>
                        AI
                      </button>
                      <button onClick={handleToggleBestMoves} disabled={store.isSelfPlaying} title={t('bestNextMoves')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: store.isSelfPlaying ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: store.showBestMovesOnBoard ? '#4a90e2' : '#333333' }}>
                        <FontAwesomeIcon icon={faStar} size="lg" />
                      </button>
                      <button onClick={handleToggleAlternativeMoves} disabled={store.isSelfPlaying || store.currentPosition === 0} title={t('alternativeMoves')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || store.currentPosition === 0) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: store.showAlternativeMovesOnBoard ? '#4a90e2' : ((store.isSelfPlaying || store.currentPosition === 0) ? '#BBB199' : '#333333'), fontSize: '12px', fontWeight: 'bold' }}>
                        {t('alt')}
                      </button>
                      {/* Score button hidden — not implemented yet */}
                    </div>
                    <div className="btn-icon-group">
                      <button onClick={store.goToStart} disabled={store.isSelfPlaying || store.currentPosition === 0} title={t('firstMove')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || store.currentPosition === 0) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || store.currentPosition === 0) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faBackwardStep} size="lg" />
                      </button>
                      <button onClick={() => store.goToMove(store.currentPosition - 10)} disabled={store.isSelfPlaying || store.currentPosition === 0} title={t('back10')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || store.currentPosition === 0) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || store.currentPosition === 0) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faBackward} size="lg" />
                      </button>
                      <button onClick={store.previousMove} disabled={store.isSelfPlaying || store.currentPosition === 0} title={t('prevMove')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || store.currentPosition === 0) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || store.currentPosition === 0) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                      </button>
                      <button onClick={handleNextMove} disabled={store.isSelfPlaying || !store.hasNextMove} title={t('nextMove')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.hasNextMove) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.hasNextMove) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faChevronRight} size="lg" />
                      </button>
                      <button onClick={() => store.goToMove(store.currentPosition + 10)} disabled={store.isSelfPlaying || !store.hasNextMove} title={t('next10')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.hasNextMove) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.hasNextMove) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faForward} size="lg" />
                      </button>
                      <button onClick={store.goToEnd} disabled={store.isSelfPlaying || !store.canGoToEnd} title={t('lastMove')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.canGoToEnd) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.canGoToEnd) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faForwardStep} size="lg" />
                      </button>
                    </div>
                    <div className="btn-icon-group">
                      <button onClick={store.returnToMainLine} disabled={store.isSelfPlaying || !store.canReturnToMainLine} title={t('returnToMainLine')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.canReturnToMainLine) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.canReturnToMainLine) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faCodeBranch} size="lg" />
                      </button>
                      <button onClick={store.removeLastMove} disabled={store.isSelfPlaying || !store.canRemoveMove} title={t('undo')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.canRemoveMove) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.canRemoveMove) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faRotateLeft} size="lg" />
                      </button>
                      <button onClick={handlePass} disabled={store.isSelfPlaying || !store.canPlayMove} title={t('passBtn')} className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: (store.isSelfPlaying || !store.canPlayMove) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (store.isSelfPlaying || !store.canPlayMove) ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faHand} size="lg" />
                      </button>
                      <button onClick={() => setShowBookmarkDialog(true)} disabled={store.isSelfPlaying} title="Bookmark this position" className="btn-icon" style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px', cursor: store.isSelfPlaying ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: store.isSelfPlaying ? '#BBB199' : '#333333' }}>
                        <FontAwesomeIcon icon={faBookmark} size="lg" />
                      </button>
                    </div>
                  </div>
                  {/* Self-play + bot toggle — desktop only */}
                  {!isMobile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={() => store.setSelfPlaying(!store.isSelfPlaying)}
                        title={store.isSelfPlaying ? t('pauseSelfPlay') : t('startSelfPlay')}
                        className="btn-icon"
                        style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', WebkitTapHighlightColor: 'transparent', padding: '3px', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: store.isSelfPlaying ? '#4a90e2' : '#333333' }}
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} size="lg" spin={store.isSelfPlaying} />
                      </button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: store.isSelfPlaying ? 'not-allowed' : 'pointer', pointerEvents: store.isSelfPlaying ? 'none' : undefined, opacity: store.isSelfPlaying ? 0.4 : 1 }} title={t('botMode')}>
                      <FontAwesomeIcon icon={faRobot} style={{ color: !store.settings.disable_ai ? '#4a90e2' : '#bbb', fontSize: '14px' }} />
                      <div onClick={handleToggleBotMode} style={{ position: 'relative', width: '36px', height: '20px', backgroundColor: !store.settings.disable_ai ? '#4a90e2' : '#ccc', borderRadius: '10px', cursor: 'pointer', transition: 'background-color 0.3s', flexShrink: 0 }}>
                        <div style={{ position: 'absolute', top: '2px', left: !store.settings.disable_ai ? '18px' : '2px', width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </label>
                    </div>
                  )}
                </div>
                {/* Row 2: self-play + bot toggle on mobile only */}
                {isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => store.setSelfPlaying(!store.isSelfPlaying)}
                      title={store.isSelfPlaying ? t('pauseSelfPlay') : t('startSelfPlay')}
                      className="btn-icon"
                      style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', WebkitTapHighlightColor: 'transparent', padding: '3px', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: store.isSelfPlaying ? '#4a90e2' : '#333333' }}
                    >
                      <FontAwesomeIcon icon={faArrowsRotate} size="lg" spin={store.isSelfPlaying} />
                    </button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: store.isSelfPlaying ? 'not-allowed' : 'pointer', width: 'fit-content', pointerEvents: store.isSelfPlaying ? 'none' : undefined, opacity: store.isSelfPlaying ? 0.4 : 1 }} title={t('botMode')}>
                    <FontAwesomeIcon icon={faRobot} style={{ color: !store.settings.disable_ai ? '#4a90e2' : '#bbb', fontSize: '14px' }} />
                    <div onClick={handleToggleBotMode} style={{ position: 'relative', width: '36px', height: '20px', backgroundColor: !store.settings.disable_ai ? '#4a90e2' : '#ccc', borderRadius: '10px', cursor: 'pointer', transition: 'background-color 0.3s', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: '2px', left: !store.settings.disable_ai ? '18px' : '2px', width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </label>
                  </div>
                )}
              </div>}

              {/* Info row: move number + score (study mode only) — fixed height to prevent board shift */}
              {!isPlayMode && (
                <div style={{ marginTop: '4px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', color: '#999' }}>
                  <span style={{ width: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {store.isWaitingForBot && <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#888' }} />}
                  </span>
                  {store.moves.length > 0 && <span>{store.currentPosition} / {store.moves.length}</span>}
                  {lastScoreStringRef.current && <span style={{ color: '#555' }}>· {lastScoreStringRef.current}</span>}
                  {lastMoveEmojiRef.current && <span>{lastMoveEmojiRef.current}</span>}
                </div>
              )}

              <input ref={fileInputRef} type="file" accept=".sgf" style={{ display: 'none' }} onChange={handleLoadSgf} />
            </div>{/* end drag-drop wrapper */}
          </div>{/* end board container */}

        {/* Right side - desktop only inline panel */}
        {!isPlayMode && !isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', width: '280px', flexShrink: 0 }}>
            <div style={{ height: '480px', display: 'flex', flexDirection: 'column' }}>
              {store.isGameActive && (
                <div style={{ backgroundColor: '#FFF', border: '2px solid #8B7355', borderRadius: '8px', padding: '8px', marginBottom: '12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <div><strong>⚫</strong> {store.computedPlayerBlack}</div>
                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                    {store.gameResult && !store.gameResult.toLowerCase().includes('incomplete') ? store.gameResult : ''}
                  </div>
                  <div><strong>⚪</strong> {store.computedPlayerWhite}</div>
                </div>
              )}
              <RightPanelTabs onMoveClick={handleBadMoveClick} bookmarkRefreshKey={bookmarkRefreshKey} />
            </div>
          </div>
        )}

        </div>{/* end board+panel row */}

        {/* Winrate chart — shown when there are scored moves */}
        {store.moves.some(m => m.p != null) && (() => {
          const pts: WinratePoint[] = store.moves
            .map((m, i) => m.p != null ? ({ moveNum: i + 1, winprob: m.p, score: m.score, move: m.mv }) : null)
            .filter(Boolean) as WinratePoint[];
          return pts.length > 1 ? (
            <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '780px', marginTop: '12px' }}>
              <WinrateChart
                points={pts}
                currentMove={store.currentPosition}
                onMoveClick={n => store.goToMove(n)}
                height={130}
              />
            </div>
          ) : null;
        })()}

      </div>{/* end main content wrapper */}

        {/* Mobile slide-in drawer */}
        {!isPlayMode && isMobile && showMobilePanel && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setShowMobilePanel(false)}
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 100 }}
            />
            {/* Drawer */}
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '85vw', maxWidth: '320px', backgroundColor: '#fff', zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 16px rgba(0,0,0,0.2)' }}>
              {/* Drawer header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #ddd' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <SystemStatus />
                </div>
                <button onClick={() => setShowMobilePanel(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#555' }}>✕</button>
              </div>
              {/* Game info */}
              {store.isGameActive && (
                <div style={{ backgroundColor: '#FFF', border: '2px solid #8B7355', borderRadius: '8px', margin: '12px', padding: '8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <div><strong>⚫</strong> {store.computedPlayerBlack}</div>
                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                    {store.gameResult && !store.gameResult.toLowerCase().includes('incomplete') ? store.gameResult : ''}
                  </div>
                  <div><strong>⚪</strong> {store.computedPlayerWhite}</div>
                </div>
              )}
              {/* Tabs */}
              <div style={{ flex: 1, overflow: 'hidden', margin: '0 12px 12px' }}>
                <RightPanelTabs onMoveClick={(n) => { handleBadMoveClick(n); setShowMobilePanel(false); }} bookmarkRefreshKey={bookmarkRefreshKey} />
              </div>
            </div>
          </>
        )}

      {/* Bookmark dialog */}
      {showBookmarkDialog && (
        <BookmarkDialog
          moveNumber={store.currentPosition}
          onSave={handleSaveBookmark}
          onCancel={() => setShowBookmarkDialog(false)}
        />
      )}

      {/* New game dialog */}
      {showNewGameDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ backgroundColor: 'white', padding: isMobile ? '16px' : '30px', borderRadius: '8px', width: isMobile ? '90vw' : '360px', fontSize: '14px' }}>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '18px' }}>{t('newGameTitle')}</h2>

            {/* Mode */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('mode')}</strong>
              <select value={selectedMode} onChange={(e) => setSelectedMode(e.target.value as 'play' | 'study')} style={{ padding: '5px' }}>
                <option value="study">{t('studyMode')}</option>
                <option value="play">{t('playMode')}</option>
              </select>
            </div>

            {/* Handicap */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('handicap')}</strong>
              <select value={selectedHandicap} onChange={(e) => { const v = Number(e.target.value); setSelectedHandicap(v); localStorage.setItem('deepgo_handicap', String(v)); }} style={{ padding: '5px' }}>
                {[0, 2, 3, 4, 5, 6, 7, 8, 9].map((h) => (
                  <option key={h} value={h}>{h === 0 ? t('evenGame') : `${h} ${t('stones')}`}</option>
                ))}
              </select>
            </div>

            {/* Komi */}
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('komi')}</strong>
              <select value={selectedKomi} onChange={(e) => { const v = Number(e.target.value); setSelectedKomi(v); localStorage.setItem('deepgo_komi', String(v)); }} style={{ padding: '5px' }}>
                <option value={0.5}>0.5</option>
                <option value={5.5}>5.5</option>
                <option value={6.5}>6.5</option>
                <option value={7.5}>7.5</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewGameDialog(false)}>{t('cancel')}</button>
              <button onClick={handleNewGame}>{t('startGame')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(GamePlay);